import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight, Zap, Shield, Truck, Award, Building2, CreditCard, Lock } from 'lucide-react';
import { SHIPPING_NOTICE, CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-neutral-950">
      {/* Benefits Strip - Cyan Gradient */}
      <div className="bg-gradient-to-r from-axon-700 via-axon-600 to-axon-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Truck, title: 'Free Shipping', desc: 'Orders $99+' },
              { icon: Shield, title: 'Lab Tested', desc: 'Third-party verified' },
              { icon: Zap, title: 'Premium Quality', desc: 'Science-backed' },
              { icon: Award, title: '30-Day Returns', desc: 'Hassle-free' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{item.title}</p>
                  <p className="text-axon-200 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-axon-600 to-axon-500 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-display text-xl font-bold text-white tracking-wide uppercase">
                  Axon
                </span>
                <span className="text-[10px] font-semibold text-axon-500 tracking-[0.2em] uppercase">
                  Muscle
                </span>
              </div>
            </Link>
            <p className="text-neutral-400 text-sm leading-relaxed mb-6 max-w-xs">
              Premium supplements engineered for serious athletes. Science-backed formulas for maximum performance and results.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-lg">
              <Truck className="w-4 h-4 text-axon-500" />
              <span className="text-neutral-300 text-sm">{SHIPPING_NOTICE}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-display font-bold text-sm uppercase tracking-wider mb-6">Shop</h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/shop" 
                  className="text-neutral-400 hover:text-axon-500 transition-colors text-sm flex items-center gap-2 group"
                >
                  All Products
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
              {categories.slice(0, 4).map((cat) => (
                <li key={cat.id}>
                  <Link 
                    to={`/shop?category=${cat.slug}`} 
                    className="text-neutral-400 hover:text-axon-500 transition-colors text-sm flex items-center gap-2 group"
                  >
                    {cat.name}
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-display font-bold text-sm uppercase tracking-wider mb-6">Support</h3>
            <ul className="space-y-3">
              {[
                { name: 'Shipping Info', path: '/shipping' },
                { name: 'Returns & Refunds', path: '/returns' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms & Conditions', path: '/terms' },
                { name: 'Contact Us', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-neutral-400 hover:text-axon-500 transition-colors text-sm flex items-center gap-2 group"
                  >
                    {link.name}
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://affiliate.axonmuscle.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-axon-500 transition-colors text-sm flex items-center gap-2 group"
                >
                  Affiliate Program
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-4">
            <h3 className="text-white font-display font-bold text-sm uppercase tracking-wider mb-6">Contact Us</h3>
            <div className="space-y-3">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-3 p-3 bg-neutral-900 hover:bg-neutral-800 rounded-xl transition-colors border border-neutral-800 group"
              >
                <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                  <Phone className="w-4 h-4 text-axon-500" />
                </div>
                <div>
                  <p className="text-neutral-500 text-xs">Phone</p>
                  <p className="text-white font-medium text-sm">{CONTACT_INFO.phone}</p>
                </div>
              </a>
              
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-3 p-3 bg-neutral-900 hover:bg-neutral-800 rounded-xl transition-colors border border-neutral-800 group"
              >
                <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-axon-500" />
                </div>
                <div>
                  <p className="text-neutral-500 text-xs">Email</p>
                  <p className="text-white font-medium text-sm">{CONTACT_INFO.email}</p>
                </div>
              </a>
              
              <div className="flex items-start gap-3 p-3 bg-neutral-900 rounded-xl border border-neutral-800">
                <div className="w-10 h-10 bg-axon-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-axon-500" />
                </div>
                <div>
                  <p className="text-neutral-500 text-xs">Address</p>
                  <p className="text-white font-medium text-sm leading-relaxed">
                    {CONTACT_INFO.address.street}<br />
                    {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Payment Methods */}
          <div className="flex flex-col items-center mb-6 pb-6 border-b border-neutral-800">
            <p className="text-neutral-500 text-sm mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              We Accept
            </p>
            <div className="flex items-center gap-4">
              {/* Visa */}
              <svg className="h-6 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="750" height="471" rx="40" fill="#1A1F71"/>
                <path d="M278.198 334.228L311.058 138.432H364.028L331.158 334.228H278.198ZM524.308 142.688C513.958 138.792 497.468 134.528 477.168 134.528C424.888 134.528 388.028 162.108 387.698 201.628C387.368 231.208 414.618 247.698 435.248 257.388C456.538 267.408 463.778 273.688 463.668 282.618C463.558 296.218 447.288 302.278 432.088 302.278C411.018 302.278 399.798 299.038 382.428 291.358L375.518 288.008L367.928 333.038C380.418 338.618 403.378 343.468 427.228 343.688C482.808 343.688 518.888 316.548 519.328 274.568C519.548 250.828 504.568 232.658 472.818 217.718C453.728 208.238 441.898 201.848 442.008 192.258C442.008 183.658 451.918 174.398 473.198 174.398C490.898 174.068 503.828 177.858 514.068 181.758L518.998 184.008L526.478 140.408L524.308 142.688ZM661.628 138.432H620.028C607.208 138.432 597.628 141.888 591.708 154.598L513.628 334.228H569.098L580.318 304.208H647.878L654.238 334.228H703.198L661.628 138.432ZM596.208 264.878C600.538 253.698 617.578 208.458 617.578 208.458C617.248 209.008 621.798 197.278 624.448 189.818L628.008 206.858C628.008 206.858 638.568 255.598 640.888 264.878H596.208ZM232.898 138.432L181.068 267.958L175.578 241.698C166.098 210.098 136.958 175.768 104.208 158.618L151.578 333.898H207.488L288.478 138.432H232.898Z" fill="white"/>
                <path d="M131.918 138.432H45.7784L45.0084 142.578C112.188 159.728 156.548 199.908 175.578 241.698L156.108 154.928C152.658 142.328 143.188 138.872 131.918 138.432Z" fill="#F9A51A"/>
              </svg>
              {/* Mastercard */}
              <svg className="h-8 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="152" height="100" rx="8" fill="#ffffff"/>
                <circle cx="60" cy="50" r="30" fill="#EB001B"/>
                <circle cx="92" cy="50" r="30" fill="#F79E1B"/>
                <path d="M76 25.6C83.3 31.3 88 40.1 88 50C88 59.9 83.3 68.7 76 74.4C68.7 68.7 64 59.9 64 50C64 40.1 68.7 31.3 76 25.6Z" fill="#FF5F00"/>
              </svg>
              {/* American Express */}
              <svg className="h-6 w-auto" viewBox="0 0 750 471" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="750" height="471" rx="40" fill="#006FCF"/>
                <path d="M0 373.625V296.25H41.875L50 312.375L58.375 296.25H365.625V369.125C365.625 369.125 359.125 373.625 350.625 373.625H213.5L204.125 362.5V373.625H166.375V352.5C166.375 352.5 162.625 355.625 153.625 355.625H141.75V373.625H58.125L50 357.125L41.625 373.625H0Z" fill="white"/>
                <path d="M0 97.375L18.75 51.625H61.25L71.125 73.625V51.625H124.375L132.75 74.625L140.875 51.625H365.625V60.375C365.625 60.375 355.875 51.625 339.625 51.625H236.125L226.25 73.625V51.625H179.625L171.25 74.625L163.125 51.625H119.5L110.875 73.625V51.625H72.625L50 105.625L27.375 51.625H0V97.375Z" fill="white"/>
                <text x="400" y="280" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="90">AMEX</text>
              </svg>
              {/* Discover */}
              <svg className="h-6 w-auto" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="780" height="500" rx="40" fill="#4D4D4D"/>
                <path d="M470.8 250C470.8 326.6 408.9 388.8 332.1 388.8C304.1 388.8 278 380.1 256.2 365.4L349.6 111.2C415.6 133.6 470.8 186.3 470.8 250Z" fill="#F47216"/>
                <text x="100" y="270" fill="white" fontFamily="Arial" fontWeight="bold" fontSize="72">DISCOVER</text>
              </svg>
            </div>
            <p className="text-neutral-600 text-xs mt-4 flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              SSL Secured 128-bit encryption
            </p>
          </div>

          {/* Card Statement Notice */}
          <div className="text-center mb-6 pb-6 border-b border-neutral-800">
            <p className="text-neutral-500 text-xs">
              All charges will appear as "<span className="text-axon-500 font-semibold">{CARD_DESCRIPTOR}</span>" on your credit card statement.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <p className="text-neutral-500 text-sm">
                © {currentYear} Axon Muscle. All rights reserved.
              </p>
              {/* Corporate Name */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 rounded-lg border border-neutral-800">
                <Building2 className="w-3.5 h-3.5 text-axon-500" />
                <span className="text-neutral-400 text-xs font-medium">{CONTACT_INFO.corpName}</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-neutral-500 hover:text-axon-500 text-sm transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-neutral-500 hover:text-axon-500 text-sm transition-colors">
                Terms
              </Link>
              <Link to="/contact" className="text-neutral-500 hover:text-axon-500 text-sm transition-colors">
                Contact
              </Link>
              <a
                href="https://affiliate.axonmuscle.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-500 hover:text-axon-500 text-sm transition-colors"
              >
                Affiliate
              </a>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
